import {
  buildPath,
  buildTemplatePath,
  CrudMode,
  Grid,
  Page,
  PageRoute,
  useElementBreakpoints,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Developer, DeveloperEditResource, DeveloperViewResource, Permission } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { BasicInfo } from "./BasicInfo";
import { DatesSection } from "./DatesSection";
import { DeveloperPageHeader } from "./DeveloperPageHeader";

const routeName = "developer";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

export const developerPageRoute: PageRoute<Params> = {
  handle: "Developer",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const DeveloperPage = () => {
  const ROOT_PERMISSION: Permission = Permission.Developer;
  const navigate = useNavigate();
  const { params } = useParams(routeName, Params);
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
  const [developer, setDeveloper] = useState<Developer>({} as Developer);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [developer] });
  const [viewResource, setViewResource] = useState<DeveloperViewResource>({});
  const [editResource, setEditResource] = useState<DeveloperEditResource>({});
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);

    ApiService.developers.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") {
      ApiService.developers.getEditResource().then((response) => {
        setEditResource(response);
      });
    }
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${developerPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.developers.get(id).then((response) => {
      setDeveloper(response);
      resetUnsavedChanges([response]);
    });
  };

  return (
    <Page
      isForm
      header={
        <DeveloperPageHeader
          rootPermission={ROOT_PERMISSION}
          handle={developerPageRoute.handle}
          crudMode={params.crudMode}
          reroute={reroute}
          fetch={fetch}
          developer={developer}
          setValidationErrors={setValidationErrors}
          unsavedChanges={unsavedChanges}
        />
      }
    >
      <Grid ref={breakpointsRef} container spacing="md">
        <Grid item xs={setBreakpoints({ md: 8, xs: 12 })}>
          <BasicInfo
            crudMode={params.crudMode}
            developer={developer}
            setDeveloper={setDeveloper}
            viewResource={viewResource}
            editResource={editResource}
            validationErrors={validationErrors}
          />
        </Grid>

        <Grid item xs={setBreakpoints({ md: 4, xs: 12 })}>
          <DatesSection
            crudMode={params.crudMode}
            developer={developer}
            setDeveloper={setDeveloper}
            viewResource={viewResource}
            validationErrors={validationErrors}
          />
        </Grid>
      </Grid>

      <Outlet />
    </Page>
  );
};
